/**
 * OpenClaw Gateway WebSocket JSON-RPC Client
 *
 * Connects to the openclaw gateway via WebSocket and speaks its native
 * JSON-RPC protocol (connect challenge → connect → request/response).
 *
 * Usage:
 *   const client = new GatewayClient('ws://192.168.1.100:18789', 'token');
 *   await client.connect();
 *   const files = await client.request('agents.files.list', { agentId: 'main' });
 *   client.close();
 */

let _idCounter = 0;
function nextId() {
  return `req-${++_idCounter}-${Date.now()}`;
}

export class GatewayClient {
  constructor(wsUrl, token) {
    // Convert http(s) URL to ws(s) if needed, strip hash fragment
    const base = wsUrl.split('#')[0].replace(/^http/, 'ws').replace(/\/$/, '');
    this.wsUrl = base;
    this.token = token || '';
    this.ws = null;
    this._pending = new Map();
    this._connected = false;
    this._connectPromise = null;
  }

  get connected() {
    return this._connected;
  }

  /** Open WebSocket and complete the connect handshake. Resolves when ready. */
  connect() {
    if (this._connectPromise) return this._connectPromise;
    this._connectPromise = new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('gateway connect timeout'));
        this.close();
      }, 4000);

      this.ws = new WebSocket(this.wsUrl);

      this.ws.onmessage = (ev) => {
        let frame;
        try { frame = JSON.parse(ev.data); } catch { return; }

        // Step 1: server sends connect.challenge with nonce
        if (frame.type === 'event' && frame.event === 'connect.challenge') {
          const nonce = frame.payload?.nonce || '';
          this._sendConnect(nonce);
          return;
        }

        // Step 3: response to our "connect" request
        if (frame.type === 'res') {
          const pending = this._pending.get(frame.id);
          if (pending) {
            this._pending.delete(frame.id);
            if (frame.ok) {
              pending.resolve(frame.payload);
            } else {
              pending.reject(new Error(frame.error?.message || 'request failed'));
            }
          }
          return;
        }
      };

      this.ws.onerror = () => {};
      this.ws.onclose = () => {
        this._connected = false;
        this._flushPending(new Error('gateway closed'));
      };

      // The connect handshake is done via request('connect', ...) which
      // resolves when the server responds. We hook into that.
      this._onConnectSuccess = () => {
        clearTimeout(timeout);
        this._connected = true;
        resolve();
      };
      this._onConnectFail = (err) => {
        clearTimeout(timeout);
        reject(err);
      };
    });
    return this._connectPromise;
  }

  /** Step 2: send the connect frame after receiving challenge nonce */
  _sendConnect(nonce) {
    const id = nextId();
    const frame = {
      type: 'req',
      id,
      method: 'connect',
      params: {
        minProtocol: 3,
        maxProtocol: 3,
        client: {
          id: 'openclaw-control-ui',
          version: '1.0.0',
          platform: 'web',
          mode: 'webchat',
        },
        role: 'operator',
        scopes: ['operator.admin', 'operator.read', 'operator.write', 'operator.approvals', 'operator.pairing'],
        auth: { token: this.token || undefined },
      },
    };
    const p = new Promise((resolve, reject) => {
      this._pending.set(id, { resolve, reject });
    });
    this.ws.send(JSON.stringify(frame));
    p.then((hello) => {
      console.log('[GatewayClient] connected, hello:', JSON.stringify(hello));
      this._onConnectSuccess?.();
    })
     .catch((err) => this._onConnectFail?.(err));
  }

  /** Send a JSON-RPC request and return the response payload. */
  request(method, params) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return Promise.reject(new Error('gateway not connected'));
    }
    const id = nextId();
    const frame = { type: 'req', id, method, params };
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this._pending.delete(id);
        reject(new Error(`gateway request timeout: ${method}`));
      }, 30000);
      this._pending.set(id, {
        resolve: (v) => { clearTimeout(timer); resolve(v); },
        reject: (e) => { clearTimeout(timer); reject(e); },
      });
      this.ws.send(JSON.stringify(frame));
    });
  }

  close() {
    this._connected = false;
    this._connectPromise = null;
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }
    this._flushPending(new Error('client closed'));
  }

  _flushPending(err) {
    for (const [, p] of this._pending) {
      p.reject(err);
    }
    this._pending.clear();
  }
}

function buildCustomHostWsUrl(device) {
  const customHost = (device?.customHost || '').trim();
  if (!customHost) return '';
  if (/^https?:\/\//i.test(customHost)) {
    const url = new URL(customHost);
    url.protocol = url.protocol === 'http:' ? 'ws:' : 'wss:';
    url.pathname = '';
    url.search = '';
    url.hash = '';
    return url.toString().replace(/\/$/, '');
  }
  const wsProto = device.protocol === 'http' ? 'ws' : 'wss';
  return `${wsProto}://${customHost}`;
}

/**
 * Helper: create a connected GatewayClient from a device record.
 * @param {Object} device - device record from /device/list
 * @returns {Promise<GatewayClient>}
 */
export async function createGatewayClientFromDevice(device) {
  const url = buildCustomHostWsUrl(device);
  const token = device.gatewayToken || '';
  if (!url) {
    throw new Error('设备未配置 customHost，无法连接 OpenClaw 网关');
  }

  const client = new GatewayClient(url, token);
  try {
    await client.connect();
    return client;
  } catch (e) {
    client.close();
    throw new Error(`无法连接到 OpenClaw 网关\n${url}: ${e.message}`);
  }
}
